import { isObject } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { WAITING_TIME } from 'coreOld/constants';
import { ApiStatuses, FormActions, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { IMessage } from 'coreOld/types';
import { getNotificationMessage } from 'coreOld/utils';

export const useHandleErrors = (
  successMessage: IMessage | string | null,
  loadingMessage: IMessage | string | null,
  notificationPosition = NotificationPosition.MODAL,
  invalidateQueries?: string[],
  formAction?: FormActions,
  errorMessageFormat?: string,
  getProcessedErrorMessage?: (response?) => string | JSX.Element
) => {
  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotification();
  const queryClient = useQueryClient();

  const isSuccessIMessageType = isObject(successMessage);
  const isLoadingIMessageType = isObject(loadingMessage);

  const errors = {
    onSuccess: (data, context) => {
      const response = data?.data || data;
      const isSuccess = response?.success || true;

      if (invalidateQueries) {
        setTimeout(() => {
          invalidateQueries.forEach((key) => queryClient.invalidateQueries(key));
        }, WAITING_TIME);
      }

      if (isSuccess && successMessage) {
        showNotification({
          message: isSuccessIMessageType
            ? getNotificationMessage(context, successMessage, formAction || undefined)
            : successMessage,
          type: ApiStatuses.SUCCESS,
          position: notificationPosition,
        });

        return;
      }

      if (!isSuccess || response?.error || response?.errors) {
        const message = response?.error || response?.errors || t('serverError');
        const processedMessage = getProcessedErrorMessage && getProcessedErrorMessage(response);

        showNotification({
          message: processedMessage || message,
          type: ApiStatuses.ERROR,
          position: notificationPosition,
        });

        return;
      }

      if (!successMessage) {
        clearNotification();
      }
    },

    onMutate: (data) => {
      if (!loadingMessage) {
        return;
      }

      showNotification({
        message: isLoadingIMessageType
          ? getNotificationMessage(data, loadingMessage, formAction, true)
          : loadingMessage,
        type: ApiStatuses.LOADING,
        position: notificationPosition,
      });
    },

    onError: (error: any) => {
      showNotification({
        message:
          t(errorMessageFormat, {
            error: error.response?.data?.error,
          }) ||
          error.response?.data?.error ||
          t('serverError'),
        type: ApiStatuses.ERROR,
        position: notificationPosition,
      });
    },
  };

  return { errors };
};
